import { Rect } from "client/Misc/commonTypes";
import {
	PropsWithChildren,
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./VisibleAreaTracker.module.less";
import {
	Deferred,
	deferred,
	ensureNotNil,
	isNil,
	setTrackingId,
	isNotNil
} from "client/Misc/utils";
import { shallowComparison } from "client/Misc/compareUtils";
import {
	useStableImperativeCallback,
	useSynchronizedRef
} from "client/Hooks";
import { never } from "client_legacy/ndaw/errors";
import { registerSizeListener, SizeInformation } from "client_legacy/ndaw/sizing";

export type VisibleAreaTrackerProps = {
	onChange(area: Rect): void;
	trackingId: string | undefined;
} & (
	| {
		direction: "horizontal";
		width: number | "auto";
		height?: undefined;
	}
	| {
		direction: "vertical";
		width?: undefined;
		height: number | "auto";
	}
	| {
		direction: "both";
		width: number | "auto";
		height: number | "auto";
	}
);

export const VisibleAreaTracker = (props: PropsWithChildren<VisibleAreaTrackerProps>): ReactElement => {
	const {
		direction,
		width: widthFromProps,
		height: heightFromProps,
		onChange,
		trackingId,
		children
	} = props;

	const [measuredSize, setMeasuredSize] = useState<SizeInformation | null>(null);

	const visibleAreaRef: RefObject<Rect> = useRef({
		left: 0,
		top: 0,
		right: 0,
		bottom: 0
	});

	const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const detectorsContainerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const postponedDetectionHandleRef: RefObject<SetTimeoutHandle | null> = useRef<SetTimeoutHandle>(null);

	// NOTE: This observer should be initialized immediately and not in the effect because it is passed into the hooks
	// This is why its type is not nullable, and there is initialization following the definition directly.
	const detectionObserverRef: RefObject<IntersectionObserver> = useRef<IntersectionObserver>(null!);

	if (detectionObserverRef.current === null) {
		detectionObserverRef.current = new IntersectionObserver(
			() => detectVisibleArea(),
			{
				// On chrome when the element is moved outside of
				// the visible area (not the view port) E.g using css transform
				// on the way back the last threshold does not trigger intersection event.
				// And by default there is only single threshold of "0", which does not trigger intersection event.
				// This causes issues in some cases E.g. virtual lists after going out of
				// the visible area and back will be broken and items won't update.
				// We add an extra threshold to ensure that the threshold of "0" always triggers.
				// Even though the second threshold can be of any value,
				// we picked the one which is really close to "0" to minimize the chance
				// of event triggering multiple times.
				threshold: [0, 0.0001]
			}
		);
	}

	const shouldMeasureDimensions: boolean = widthFromProps === "auto"
		|| heightFromProps === "auto";

	useLayoutEffect(
		(): EffectWithDestructorResult => {
			if (!shouldMeasureDimensions) {
				return;
			}

			const container: HTMLDivElement = ensureNotNil(containerRef.current);

			return registerSizeListener(
				container,
				setMeasuredSize
			);
		},
		[shouldMeasureDimensions]
	);

	const width: number | undefined = widthFromProps === "auto"
		? measuredSize?.width
		: widthFromProps;

	const height: number | undefined = heightFromProps === "auto"
		? measuredSize?.height
		: heightFromProps;

	const leftEdgeDetectorRef: RefObject<EdgeDetectorHandlers | null> = useEdgeDetector({
		containerRef: detectorsContainerRef,
		maxValue: direction === "horizontal" || direction === "both"
			? width
			: undefined,
		observer: detectionObserverRef.current,
		variant: "horizontal",
		initialPosition: visibleAreaRef.current.left
	});

	const topEdgeDetectorRef: RefObject<EdgeDetectorHandlers | null> = useEdgeDetector({
		containerRef: detectorsContainerRef,
		maxValue: direction === "vertical" || direction === "both"
			? height
			: undefined,
		observer: detectionObserverRef.current,
		variant: "vertical",
		initialPosition: visibleAreaRef.current.top
	});

	const rightEdgeDetectorRef: RefObject<EdgeDetectorHandlers | null> = useEdgeDetector({
		containerRef: detectorsContainerRef,
		maxValue: direction === "horizontal" || direction === "both"
			? width
			: undefined,
		observer: detectionObserverRef.current,
		variant: "horizontal",
		initialPosition: visibleAreaRef.current.right
	});

	const bottomEdgeDetectorRef: RefObject<EdgeDetectorHandlers | null> = useEdgeDetector({
		containerRef: detectorsContainerRef,
		maxValue: direction === "vertical" || direction === "both"
			? height
			: undefined,
		observer: detectionObserverRef.current,
		variant: "vertical",
		initialPosition: visibleAreaRef.current.bottom
	});

	const getVisibleArea: (container: HTMLElement) => Promise<Rect> = useVisibleAreaResolver();
	const stableChangeHandler: typeof onChange = useStableImperativeCallback(onChange);

	// NOTE: this handler imperatively repositions detectors to the new visible area,
	// so that the detectors are precisely at the edges of the visible area.
	// It then calls detectVisibleArea handler again, because user could do more scrolling
	// while we executed this logic.
	// If visible area does not change the handler will early return, which should break the update loop.
	const applyVisibleArea = useCallback(
		(
			newValue: Rect,
			detectVisibleArea: () => void
		): void => {
			const currentValue: Rect = visibleAreaRef.current;

			if (shallowComparison(newValue, currentValue)) {
				return;
			}

			visibleAreaRef.current = newValue;

			const leftEdgeDetector: EdgeDetectorHandlers | null = leftEdgeDetectorRef.current;
			const topEdgeDetector: EdgeDetectorHandlers | null = topEdgeDetectorRef.current;
			const rightEdgeDetector: EdgeDetectorHandlers | null = rightEdgeDetectorRef.current;
			const bottomEdgeDetector: EdgeDetectorHandlers | null = bottomEdgeDetectorRef.current;

			if (leftEdgeDetector) {
				leftEdgeDetector.setPosition(newValue.left);
			}

			if (topEdgeDetector) {
				topEdgeDetector.setPosition(newValue.top);
			}

			if (rightEdgeDetector) {
				rightEdgeDetector.setPosition(newValue.right);
			}

			if (bottomEdgeDetector) {
				bottomEdgeDetector.setPosition(newValue.bottom);
			}

			if (isNotNil(postponedDetectionHandleRef.current)) {
				clearTimeout(postponedDetectionHandleRef.current);
			}

			postponedDetectionHandleRef.current = setTimeout(
				() => detectVisibleArea(),
				1
			);
		},
		[
			bottomEdgeDetectorRef,
			leftEdgeDetectorRef,
			rightEdgeDetectorRef,
			topEdgeDetectorRef
		]
	);

	const detectVisibleArea = useCallback(
		async (): Promise<void> => {
			const container: HTMLDivElement = ensureNotNil(containerRef.current);
			const visibleArea: Rect = await getVisibleArea(container);

			applyVisibleArea(visibleArea, detectVisibleArea);

			stableChangeHandler(visibleArea);
		},
		// NOTE: All the dependencies must be stable because the callback is captured by the detectionObserverRef
		[
			applyVisibleArea,
			getVisibleArea,
			stableChangeHandler
		]
	);

	// NOTE: detectionObserver also observes the container for the cases when container is originally
	// out of the viewport. In this case visible area will be empty and the edge detectors will be positioned incorrectly.
	useEffect(
		(): EffectWithDestructorResult => {
			const container: HTMLDivElement = ensureNotNil(containerRef.current);
			const detectionObserver: IntersectionObserver = detectionObserverRef.current;

			detectionObserver.observe(container);

			return (): void => detectionObserver.unobserve(container);
		},
		[]
	);

	useLayoutEffect(
		(): EffectWithDestructorResult => {
			return (): void => {
				if (isNotNil(postponedDetectionHandleRef.current)) {
					clearTimeout(postponedDetectionHandleRef.current);
					postponedDetectionHandleRef.current = null;
				}
				detectionObserverRef.current
					.disconnect();
			};
		},
		[]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			ref={containerRef}
			style={{
				width: widthFromProps,
				height: heightFromProps
			}}
			className={styles.root}
		>
			{children}
			<div ref={detectorsContainerRef} />
		</div>
	);
};
VisibleAreaTracker.displayName = "VisibleAreaTracker";

type EdgeDetectorHandlers = {
	setPosition(value: number): void;
};

type EdgeDetectorOptions = {
	containerRef: ReadonlyRefObject<HTMLElement | null>;
	observer: IntersectionObserver;
	variant: "horizontal" | "vertical";
	maxValue: number | undefined;
	initialPosition: number;
};

function useEdgeDetector(options: EdgeDetectorOptions): ReadonlyRefObject<EdgeDetectorHandlers | null> {
	const {
		containerRef,
		observer,
		variant,
		maxValue,
		initialPosition
	} = options;

	// NOTE: each detector uses two lines (1px thick div).
	// When detectors positioned correctly, one line will be visible and one is hidden
	const linesRef: RefObject<LineElements | null> = useRef<LineElements>(null);
	const initialPositionRef: ReadonlyRefObject<number> = useSynchronizedRef(initialPosition);

	useLayoutEffect(
		(): EffectWithDestructorResult => {
			if (isNil(maxValue)) {
				return undefined;
			}

			const container: HTMLElement = ensureNotNil(containerRef.current);
			const firstLine: HTMLDivElement = document.createElement("div");
			const lastLine: HTMLDivElement = document.createElement("div");

			firstLine.classList
				.add(styles.edgeDetector);
			lastLine.classList
				.add(styles.edgeDetector);

			switch (variant) {
				case "horizontal":
					firstLine.classList
						.add(styles.edgeDetector_horizontal);
					lastLine.classList
						.add(styles.edgeDetector_horizontal);
					break;
				case "vertical":
					firstLine.classList
						.add(styles.edgeDetector_vertical);
					lastLine.classList
						.add(styles.edgeDetector_vertical);
					break;
				default:
					never(variant);
			}

			applyPosition(
				variant,
				initialPositionRef.current,
				maxValue,
				firstLine,
				lastLine
			);

			container.appendChild(firstLine);
			container.appendChild(lastLine);

			linesRef.current = {
				firstLine,
				lastLine
			};

			observer.observe(firstLine);
			observer.observe(lastLine);

			return (): void => {
				observer.unobserve(firstLine);
				observer.unobserve(lastLine);

				firstLine.remove();
				lastLine.remove();
			};
		},
		[
			containerRef,
			initialPositionRef,
			maxValue,
			observer,
			variant
		]
	);

	const setPosition = useCallback(
		(value: number): void => {
			if (!linesRef.current || !maxValue) {
				return;
			}

			const {
				firstLine,
				lastLine
			} = linesRef.current;

			applyPosition(
				variant,
				value,
				maxValue,
				firstLine,
				lastLine
			);
		},
		[maxValue, variant]
	);

	const handlers: EdgeDetectorHandlers | null = useMemo(
		(): EdgeDetectorHandlers | null => {
			if (isNil(maxValue)) {
				return null;
			}

			return {
				setPosition
			};
		},
		[
			maxValue,
			setPosition
		]
	);

	return useSynchronizedRef(handlers);
}

// NOTE Margin (ie offset of the line from the edge) is set to 2px. Ideally it should be 1px
// But when I set it to 1px there was a bug in chrome, because even though element was clearly out
// of the visible area, it was still detected by observer as visible.
// I assumed that there is some precision issue in calculation, and with margin of 2px
// I didn't experience the issue in any browser any more
const edgeDetectorMargin = 2;

function applyPosition(
	variant: "horizontal" | "vertical",
	position: number,
	maxValue: number,
	firstLine: HTMLDivElement,
	lastLine: HTMLDivElement
): void {
	applyPositionToLine(
		variant,
		position - edgeDetectorMargin,
		maxValue,
		firstLine
	);

	applyPositionToLine(
		variant,
		position + edgeDetectorMargin,
		maxValue,
		lastLine
	);
}

function applyPositionToLine(
	variant: "horizontal" | "vertical",
	position: number,
	maxValue: number,
	line: HTMLDivElement
): void {
	const isOutOfBound: boolean = position < 0 || position > maxValue;

	const lineStyles: CSSStyleDeclaration = line.style;

	lineStyles.display = isOutOfBound
		? "none"
		: "";

	switch (variant) {
		case "horizontal":
			lineStyles.left = `${position}px`;
			lineStyles.top = "";
			break;
		case "vertical":
			lineStyles.left = "";
			lineStyles.top = `${position}px`;
			break;
		default:
			never(variant);
	}
}

type LineElements = {
	firstLine: HTMLDivElement;
	lastLine: HTMLDivElement;
};

// NOTE: This hook provides a method which returns a visible area of the requested container
// It uses the IntersectionObserver for this, because its event has already calculated value
// which takes into account margins paddings scrolls positions etc.
// It triggers event once by starting observing the container, and then after area is calculated
// it immediately unobserves the container.
function useVisibleAreaResolver(): ((container: HTMLElement) => Promise<Rect>) {
	const calculationObserverRef: RefObject<IntersectionObserver | null> = useRef<IntersectionObserver>(null);
	const [containerToPromiseMap] = useState<WeakMap<Element, Deferred<Rect>>>(() => new WeakMap());

	const getVisibleArea = useCallback(
		(container: HTMLElement): Promise<Rect> => {
			if (containerToPromiseMap.has(container)) {
				const containerDeferred: Deferred<Rect> = containerToPromiseMap.get(container)!;
				return containerDeferred.promise;
			}

			const observer: IntersectionObserver = ensureNotNil(calculationObserverRef.current);

			observer.observe(container);

			const deferredResult: Deferred<Rect> = deferred<Rect>();

			containerToPromiseMap.set(container, deferredResult);

			return deferredResult.promise;
		},
		[containerToPromiseMap]
	);

	useLayoutEffect(
		(): EffectWithDestructorResult => {
			const calculationObserver = new IntersectionObserver(
				(entries: IntersectionObserverEntry[]): void => {
					for (const entry of entries) {
						calculationObserver.unobserve(entry.target);

						if (!containerToPromiseMap.has(entry.target)) {
							continue;
						}

						const containerDeferred: Deferred<Rect> = containerToPromiseMap.get(entry.target)!;
						containerToPromiseMap.delete(entry.target);

						const {
							intersectionRect,
							boundingClientRect
						} = entry;

						const left: number = intersectionRect.left - boundingClientRect.left;
						const top: number = intersectionRect.top - boundingClientRect.top;
						const width: number = intersectionRect.width;
						const height: number = intersectionRect.height;

						const newArea: Rect = {
							left,
							right: left + width,
							top,
							bottom: top + height
						};

						containerDeferred.resolve(newArea);
					}
				}
			);

			calculationObserverRef.current = calculationObserver;

			return (): void => {
				calculationObserver.disconnect();
				calculationObserverRef.current = null;
			};
		},
		[containerToPromiseMap]
	);

	return getVisibleArea;
}